import { useEffect, useRef } from "react";

/**
 * Hold a screen wake lock while `active` is true.
 *
 * The browser releases the lock automatically when the tab is hidden, so we
 * re-request it on visibilitychange — a quick switch to another app shouldn't
 * drop the lock for the rest of the round.
 *
 * Silent no-op on browsers without the Wake Lock API (Safari < 16.4, older
 * Firefox, etc).
 */
export function useWakeLock(active: boolean) {
    const sentinelRef = useRef<WakeLockSentinel | null>(null);

    useEffect(() => {
        if (!active || !("wakeLock" in navigator)) return;

        let cancelled = false;

        const acquire = async () => {
            if (document.visibilityState !== "visible") return;
            if (sentinelRef.current) return;
            try {
                const s = await navigator.wakeLock.request("screen");
                if (cancelled) {
                    s.release().catch(() => {});
                    return;
                }
                sentinelRef.current = s;
                s.addEventListener("release", () => {
                    if (sentinelRef.current === s) sentinelRef.current = null;
                });
            } catch {
                /* battery saver / permission denied / etc — keep silent */
            }
        };

        const onVisible = () => {
            if (document.visibilityState === "visible") void acquire();
        };

        void acquire();
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            cancelled = true;
            document.removeEventListener("visibilitychange", onVisible);
            const s = sentinelRef.current;
            sentinelRef.current = null;
            if (s) s.release().catch(() => {});
        };
    }, [active]);
}
